import { useState, useEffect } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ExportOptions from "../../../ExportOptions";
import ConfirmActionModal from "../../../ConfirmDeleteModal";
import TableSkeleton from "../../../TableSkeleton";
import EmergencyServiceModal from "./EmergencyServiceModal";

const EmergencyServiceList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const itemsPerPage = 10;

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://api.muktihospital.com/api/emergency/services", {
        headers: {
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
      });
      const sanitizedServices = response.data.services.map((service) => ({
        id: service.id,
        serviceName: service.serviceName || "",
        particulars: service.particulars || "",
        providedBy: service.providedBy || "",
        unitPrice: service.unitPrice || 0,
      }));
      setServices(sanitizedServices);
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Failed to fetch services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [refreshTrigger]);

  const filteredServices = services.filter(
    (service) =>
      service.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.particulars.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.providedBy.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

  const currentData = filteredServices
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((service, index) => ({
      ...service,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((service) => service.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (serviceId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(serviceId)
        ? prevSelectedRows.filter((id) => id !== serviceId)
        : [...prevSelectedRows, serviceId]
    );
  };

  const handleDelete = async (serviceIds) => {
    try {
      await Promise.all(serviceIds.map(id =>
        axios.delete(`https://api.muktihospital.com/api/emergency/services/delete/${id}`, {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        })
      ));
      toast.success(`${serviceIds.length} service(s) deleted successfully!`);
      setRefreshTrigger(prev => prev + 1);
      setSelectedRows([]);
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting service(s):", error);
      toast.error("Failed to delete service(s).");
    }
  };

  const handleEdit = (service) => {
    setServiceToEdit(service);
    setServiceModalOpen(true);
  };

  const handleSaveService = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["Serial No", "Service Name", "Particulars", "Provided By", "Unit Price"];
  const dataMapper = (item) =>
    [item.displayId, item.serviceName, item.particulars, item.providedBy, item.unitPrice].join("\t");

  // --- UPDATED SKELETON CONFIGURATION ---
  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    { width: "80px", height: "16px", className: "text-left py-5 px-3" }, // Serial
    { width: "150px", height: "16px", className: "text-left py-5 px-3" }, // Name
    { width: "150px", height: "16px", className: "text-left py-5 px-3" }, // Particulars
    { width: "120px", height: "16px", className: "text-left py-5 px-3" }, // Provided By
    { width: "80px", height: "16px", className: "text-left py-5 px-3" }, // Price
    { width: "80px", height: "16px", className: "text-right p-3 print-hidden-in-print", skeletonClassName: "ml-auto" },
  ];

  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        
        {/* --- HEADER SECTION --- */}
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Service List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => {
                  setServiceToEdit(null);
                  setServiceModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Service
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Service List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>

        {/* --- TABLE SECTION --- */}
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print"><div className="relative left-3 -top-[2px]"></div></th>
                      {headers.map((header, idx) => (
                         <th key={idx} className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">{header}</th>
                      ))}
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : filteredServices.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No services found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      {/* --- CUSTOM SELECT ALL CHECKBOX --- */}
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="selectAllServices"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              currentData.length > 0 &&
                              currentData.every((item) => selectedRows.includes(item.id))
                            }
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="selectAllServices"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      {/* --- HEADERS --- */}
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Serial No</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Service Name</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Particulars</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Provided By</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Unit Price</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((service, index) => (
                      <tr key={service.id} className="border-b hover:bg-gray-50">
                        {/* --- CUSTOM ROW CHECKBOX --- */}
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`service-${index}`}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(service.id)}
                              onChange={() => handleRowSelect(service.id)}
                              disabled={loading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={`service-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                            ></label>
                          </div>
                        </td>
                        
                        {/* --- DATA CELLS --- */}
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{service.displayId}</td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{service.serviceName}</td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{service.particulars || "-"}</td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{service.providedBy || "-"}</td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{service.unitPrice}</td>
                        
                        {/* --- ACTIONS --- */}
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleEdit(service)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([service.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* --- PAGINATION --- */}
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
               <div className="inline-flex items-center justify-end gap-2">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    disabled={currentPage === 1}
                    className="p-2 border rounded hover:bg-gray-100 disabled:opacity-50"
                  >
                    <MdKeyboardArrowLeft />
                  </button>
                  <span className="text-sm">Page {currentPage} of {totalPages}</span>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded hover:bg-gray-100 disabled:opacity-50"
                  >
                    <MdKeyboardArrowRight />
                  </button>
               </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => handleDelete(selectedRows)}
        ids={selectedRows}
        entityName="service"
        title="Confirm Delete"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} service(s)?`
            : `Do you really want to delete this service?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <EmergencyServiceModal
        isOpen={serviceModalOpen}
        onClose={() => {
            setServiceModalOpen(false);
            setServiceToEdit(null);
        }}
        onSave={handleSaveService}
        serviceToEdit={serviceToEdit}
      />
    </div>
  );
};

export default EmergencyServiceList;